import 'bootstrap'
import '../css/main.scss'
import * as React from 'react'
import { ApolloClient, ApolloProvider, InMemoryCache } from '@apollo/client'
import { persistCache, LocalStorageWrapper } from 'apollo3-cache-persist'

import { b, constUndefined, constVoid, DOM, E, identity, IOO, O, pipe, RA, RNEA, s, tt, T } from '@packages/prelude'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
// eslint-disable-next-line import/no-unresolved
import Lightbox from 'photoswipe/lightbox'
import PhotoSwipe from 'photoswipe'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
// eslint-disable-next-line import/no-unresolved
import PhotoSwipeCaption from 'photoswipe-dynamic-caption-plugin'
import { Matomo, react } from './commons'
import { App } from './App'

const prepareCache = (cache: InMemoryCache): T.Task<void> => {
  return () =>
    persistCache({
      cache,
      storage: new LocalStorageWrapper(window.localStorage),
      trigger: 'write',
      maxSize: false
    })
}

const getClient = (cache: InMemoryCache) => {
  return new ApolloClient({
    uri: '/index.php?graphql',
    cache
  })
}

window.addEventListener('load', () =>
  pipe(
    T.of({ cache: new InMemoryCache() }),
    T.chainFirst(({ cache }) => prepareCache(cache)),
    T.map(({ cache }) => getClient(cache)),
    T.chainIOK(client =>
      pipe(
        IOO.of({ document }),
        IOO.bind('postList', ({ document }) => pipe(document, DOM.querySelector('#post-list')) as IOO.IOOption<HTMLElement>),
        IOO.map(({ postList }) => {
          const filter = pipe(postList.dataset.filter, tt.NonEmptyString.decode, E.foldMap(s.Monoid)(identity))
          const hideBar = pipe(
            postList.dataset.filterBar,
            tt.NonEmptyString.decode,
            E.foldMap(b.MonoidAny)(hideBar => hideBar === 'hide')
          )
          const currentPostId = pipe(postList.dataset?.currentPostId, tt.NumberFromString.decode, E.getOrElseW(constUndefined))
          pipe(
            postList,
            react.mountTo(() => (
              <ApolloProvider client={client}>
                <App filter={filter} hideBar={hideBar} currentPostId={currentPostId} />
              </ApolloProvider>
            ))
          )
        })
      )
    )
  )()
)

// activate banner interaction tracking for Matomo
window.addEventListener('load', () =>
  pipe(
    IOO.of({ document, window }),
    IOO.bind(
      'ads',
      ({ document }) => pipe(document, DOM.querySelectorAll('a.ad')) as IOO.IOOption<RNEA.ReadonlyNonEmptyArray<HTMLLinkElement>>
    ),
    IOO.map(({ ads, window }) =>
      pipe(
        ads,
        RA.filterMap(ad =>
          pipe(
            O.Do,
            O.apS('matomoId', O.fromNullable(ad.dataset?.contentName)),
            O.apS('url', O.fromNullable(ad?.href)),
            O.apS('position', O.fromNullable(ad.dataset?.adPosition)),
            O.map(({ matomoId, url, position }) => {
              pipe(['trackContentImpression', matomoId, position, url], Matomo.configure(window))
              pipe(
                ad,
                DOM.addEventListener('click')(() => () => {
                  pipe(['trackContentInteraction', 'click', matomoId, position, url], Matomo.configure(window))
                })
              )()
            })
          )
        )
      )
    )
  )()
)

pipe(
  IOO.of({ document }),
  IOO.chain(({ document }) => pipe(document, DOM.querySelector('#gallery'))),
  IOO.match(constVoid, () => {
    const leftArrowSVGString = '<i class="fa fa-angle-left fa-3x text-primary"></i>'
    const rightArrowSVGString = '<i class="fa fa-angle-right fa-3x text-primary"></i>'
    const closeSVGString = '<i class="fa fa-xmark fa-2x text-white"></i>'
    const lightbox = new Lightbox({
      arrowPrevSVG: leftArrowSVGString,
      arrowNextSVG: rightArrowSVGString,
      closeSVG: closeSVGString,
      gallerySelector: '#gallery',
      childSelector: '.pswp-gallery__item',
      zoom: false,

      showHideAnimationType: 'fade',
      initialZoomLevel: 'fit',

      imageClickAction: 'next',
      tapAction: 'next',

      closeTitle: 'Schließen',
      arrowPrevTitle: 'Zurück',
      arrowNextTitle: 'Vor',

      errorMsg: 'Das Bild konnte nicht geladen werden',
      indexIndicatorSep: ' von ',

      pswpModule: PhotoSwipe
    })

    new PhotoSwipeCaption(lightbox, {
      type: 'below'
    })

    lightbox.init()
  })
)()

pipe(
  IOO.of({ window }),
  IOO.bind(
    'pathname',
    IOO.fromEitherK(({ window }) => pipe(window.location.pathname, tt.NonEmptyString.decode))
  ),
  IOO.bind('homeButton', ({ window: { document } }) => pipe(document, DOM.querySelector('#homeButton'))),
  IOO.map(({ pathname, homeButton }) => {
    pathname !== '/magazin/' && homeButton.classList.add('d-sm-flex')
  })
)()

pipe(
  IOO.of({ document }),
  IOO.bind(
    'referrer',
    IOO.fromEitherK(({ document }) => pipe(document.referrer, tt.NonEmptyString.decode))
  ),
  IOO.bind('backButton', ({ document }) => pipe(document, DOM.querySelector('#backButton'))),
  IOO.map(({ referrer, backButton }) => {
    referrer && backButton.classList.remove('d-none')
  })
)()

pipe(
  IOO.of({ document }),
  IOO.bind('navbar', ({ document }) => pipe(document, DOM.querySelector('.navbar'))),
  IOO.bind('navbarToggler', ({ navbar }) => pipe(navbar, DOM.querySelector('.navbar-toggler'))),
  IOO.map(({ navbar, navbarToggler }) => {
    DOM.addEventListener('click')(() => () => {
      navbar.classList.toggle('navbar-open')
    })(navbarToggler)()
  })
)()
